import { checkCustomFieldSelected } from 'containers/dispatch/utils';
import { ParsedAddress } from 'types/baseTypes';
import { CustomerV2QueryParams } from './hooks/usePaginatedGetCustomersV2Query';

/**
 * Formats billing address and cardholder information for Stripe's billing_details structure
 * @param billingAddress - The parsed address object
 * @param cardholderName - The cardholder's name
 * @param cardholderEmail - The cardholder's email
 * @returns Formatted billing details object for Stripe or undefined if no data provided
 */
export const formatBillingDetails = (
  billingAddress?: ParsedAddress,
  cardholderName?: string,
  cardholderEmail?: string
): Record<string, any> | undefined => {
  const billingDetails: Record<string, any> = {};

  if (cardholderName) {
    billingDetails.name = cardholderName;
  }

  if (cardholderEmail) {
    billingDetails.email = cardholderEmail;
  }

  if (
    billingAddress &&
    (billingAddress.street ||
      billingAddress.city ||
      billingAddress.state ||
      billingAddress.zipCode ||
      billingAddress.country)
  ) {
    billingDetails.address = {};

    if (billingAddress.street) {
      billingDetails.address.line1 = billingAddress.street;
    }

    if (billingAddress.apartment) {
      billingDetails.address.line2 = billingAddress.apartment;
    }

    if (billingAddress.city) {
      billingDetails.address.city = billingAddress.city;
    }

    if (billingAddress.state) {
      billingDetails.address.state = billingAddress.state;
    }

    if (billingAddress.zipCode) {
      billingDetails.address.postal_code = billingAddress.zipCode;
    }

    if (billingAddress.country) {
      billingDetails.address.country = billingAddress.country;
    }
  }

  return Object.keys(billingDetails).length > 0 ? billingDetails : undefined;
};

export const formatCustomerViewFiltersForAPI = (filters: Record<string, any>) => {
  const formattedFilters: Record<string, any> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          formattedFilters[key] = value;
        }
      } else if (typeof value === 'object') {
        if (Object.keys(value).length > 0) {
          formattedFilters[key] = value;
        }
      } else {
        formattedFilters[key] = value;
      }
    }
  });

  return formattedFilters;
};

export const getCustomerDateDefaultRange = () => {
  const currentDate = new Date();
  const last7DaysDate = new Date(currentDate);
  last7DaysDate.setDate(currentDate.getDate() - 7);
  const last30DaysDate = new Date(currentDate);
  last30DaysDate.setDate(currentDate.getDate() - 30);
  const last90DaysDate = new Date(currentDate);
  last90DaysDate.setDate(currentDate.getDate() - 90);

  return [
    {
      label: 'Last 7 days',
      isSelected: range => checkCustomFieldSelected(range, last7DaysDate, currentDate),
      range: () => ({
        startDate: last7DaysDate,
        endDate: currentDate
      })
    },
    {
      label: 'Last 30 days',
      isSelected: range => checkCustomFieldSelected(range, last30DaysDate, currentDate),
      range: () => ({
        startDate: last30DaysDate,
        endDate: currentDate
      })
    },
    {
      label: 'Last 90 days',
      isSelected: range => checkCustomFieldSelected(range, last90DaysDate, currentDate),
      range: () => ({
        startDate: last90DaysDate,
        endDate: currentDate
      })
    },
    {
      label: 'Custom',
      isSelected: range => {
        const isNotToday = !checkCustomFieldSelected(range, currentDate, currentDate);
        const isNotLast7Days = !checkCustomFieldSelected(range, last7DaysDate, currentDate);
        const isNotLast30Days = !checkCustomFieldSelected(range, last30DaysDate, currentDate);
        const isNotLast90Days = !checkCustomFieldSelected(range, last90DaysDate, currentDate);
        return isNotToday && isNotLast7Days && isNotLast30Days && isNotLast90Days;
      },
      range: () => ({
        startDate: currentDate,
        endDate: currentDate
      })
    }
  ];
};

export const getCustomerFullName = (customer: any): string => {
  if (!customer || typeof customer !== 'object') {
    return '';
  }

  const firstName =
    typeof customer.firstName === 'string' && customer.firstName.trim() ? customer.firstName.trim() : '';
  const lastName = typeof customer.lastName === 'string' && customer.lastName.trim() ? customer.lastName.trim() : '';
  const name = typeof customer.name === 'string' && customer.name.trim() ? customer.name.trim() : '';

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (name && lastName) {
    return `${name} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  if (lastName) {
    return lastName;
  }
  return name || '';
};

export const formatCustomerFilterParamsForAPI = (queryParams: CustomerV2QueryParams) => {
  const formattedQueryParams: Record<string, any> = {};
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && (typeof value === 'string' ? value.trim() !== '' : true)) {
      formattedQueryParams[key] = typeof value === 'string' ? value.trim() : value;
    }
  });
  return formattedQueryParams;
};
